/**
 * Labelled shared memory, AIOE-coordinated (mju-ai-workbench
 * docs/plans/2026-09-agent-nodes.md, "Shared memory for enterprise
 * projects"). The memory stays on the project's nodes; AIOE holds the
 * directory — the compartment vocabulary, who is granted what, clearances,
 * the labelling rules per project — mints each reader's entitlement into the
 * peer token, and keeps the audit of reads by label. The node's memory
 * service filters before it ranks or summarises.
 *
 * The workbench mirrors `memoryLabelSchema`, `entitlementSchema` and
 * `labellingRuleSchema` (AIOE-first; it has no copy yet).
 *
 *   GET|POST /admin/v1/compartments, DELETE /admin/v1/compartments/:id
 *   GET|PUT  /admin/v1/compartments/:id/grants          { grants: [{ subjectType, subjectId }] }
 *   GET|PUT  /admin/v1/clearances                        { clearances: [{ subjectType, subjectId, level }] }
 *   GET|PUT  /projects/v1/:id/labelling-rules            owners write; members and the project's nodes read
 *   GET      /projects/v1/:id/entitlement?user=<id>      owners: what AIOE would mint for that member
 *   GET      /projects/v1/:id/memory/unused-grants       owners: compartments granted and never read
 *   GET      /projects/v1/:id/memory/access              members: what they read; owners: what is hidden and why (`memoryAccessSchema`)
 *   GET      /memory/v1/labelling?project=<id>           a node holding one of the project's roots
 */
import { z } from "zod";
export const SENSITIVITIES = ["public", "internal", "confidential", "restricted"];
export const sensitivitySchema = z.enum(SENSITIVITIES);
/** Order of sensitivity: a reader cleared for a level reads that level and below. */
export const SENSITIVITY_RANK = { public: 0, internal: 1, confidential: 2, restricted: 3 };
/** A compartment id: lower case, `customer:acme`, `finance`. */
export const compartmentIdSchema = z.string().min(1).max(100).regex(/^[a-z0-9][a-z0-9:_.-]*$/, "lower case letters, digits and : _ . -");
/** The label on every memory item (node, community, note, document, transcript, artifact). */
export const memoryLabelSchema = z.object({
    project: z.string(),
    scope: z.enum(["repo", "knowledge"]),
    sensitivity: sensitivitySchema,
    compartments: z.array(compartmentIdSchema).max(64).default([]),
    provenance: z.object({
        source: z.string().max(2000),
        principal: z.string().optional(),
        actor: z.string().optional(),
        taskId: z.string().optional(),
        at: z.string(),
    }),
});
/** What a reader may see, minted by AIOE into the peer token (`clearance`, `compartments` claims). */
export const entitlementSchema = z.object({
    act: z.string(),
    project: z.string().optional(),
    role: z.string().optional(),
    /** The level the reader reads to: for a project's member, the higher of `organisationClearance` and `projectGrant`. */
    clearance: sensitivitySchema,
    compartments: z.array(compartmentIdSchema),
    /** Additive (Nov 2026): the person's organisation-wide clearance, which alone decides the organisation's own memory. */
    organisationClearance: sensitivitySchema.optional(),
    /** Additive (Nov 2026): a member's grant on the project, equal to its classification (Internal while it has none). */
    projectGrant: sensitivitySchema.optional(),
});
/** Whether a reader with this entitlement may see an item with this label. Deny wins. */
export function mayRead(e, label) {
    if (SENSITIVITY_RANK[label.sensitivity] > SENSITIVITY_RANK[e.clearance])
        return false;
    return label.compartments.every((c) => e.compartments.includes(c));
}
/**
 * `mayRead` plus the project check: an entitlement minted for one project reads only that
 * project's items. An item whose label names no project (memory from before labelling at
 * ingestion) belongs to `servingProject`, the project of the node serving it. An item that
 * names a project is read only by a project-scoped reader: a token with no project claim never
 * reads project memory, whatever compartments it holds. The workbench's memory service uses this
 * form.
 */
export function mayReadItem(e, label, servingProject) {
    const itemProject = label.project ?? servingProject;
    if (itemProject !== undefined && e.project === undefined)
        return false;
    if (e.project !== undefined && itemProject !== undefined && e.project !== itemProject)
        return false;
    return mayRead(e, label);
}
/** A project's rule: items whose path or source matches carry these compartments (and at least this sensitivity). */
export const labellingRuleSchema = z
    .object({
    id: z.string().min(1).max(100),
    match: z.object({ pathGlob: z.string().min(1).max(500).optional(), source: z.string().min(1).max(500).optional() }).refine((m) => Boolean(m.pathGlob) !== Boolean(m.source), {
        message: "match one of pathGlob or source",
    }),
    compartments: z.array(compartmentIdSchema).max(32).default([]),
    sensitivity: sensitivitySchema.optional(),
})
    .refine((r) => r.compartments.length > 0 || r.sensitivity, { message: "a rule sets compartments, a sensitivity, or both" });
export const labellingRulesPutSchema = z.object({ rules: z.array(labellingRuleSchema).max(500) });
export const compartmentSchema = z.object({
    id: compartmentIdSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
});
/** Who holds a compartment: a person, an IdP group, or every member of a project. */
export const compartmentGrantSchema = z.object({
    subjectType: z.enum(["user", "group", "project"]),
    subjectId: z.string().min(1).max(200),
});
export const clearanceSchema = z.object({
    subjectType: z.enum(["user", "group"]),
    subjectId: z.string().min(1).max(200),
    level: sensitivitySchema,
});
/** Everyone in the tenant reads `internal` and below unless given more (or less, per person). */
export const DEFAULT_CLEARANCE = "internal";
/** What a project's memory counts as while the project has no classification: Internal, until an owner sets one. */
export const UNCLASSIFIED_PROJECT_LEVEL = "internal";
/**
 * A project's memory level (JP, 2 Nov 2026): its classification, or Internal while it has none. It is the label of a
 * project's item that nothing else labels, and the grant every member of the project holds, so members read their
 * project's memory up to its classification. Items a rule or a person raised above it need the reader's own clearance;
 * compartments still need the compartment.
 */
export const projectMemoryLevel = (classification) => classification ?? UNCLASSIFIED_PROJECT_LEVEL;
/**
 * `GET /projects/v1/:id/memory/access` (members) and `GET /memory/v1/access` (anyone who reads the organisation's
 * memory): what the reader reads and, for the project's owners (the organisation's memory: its writers), what is hidden
 * from them and why, with the one change that would let them read most of it. Counts only, never content.
 */
export const memoryAccessSchema = z.object({
    /** The project's memory level (`projectMemoryLevel`); absent for the organisation's memory. */
    level: sensitivitySchema.optional(),
    /** False while the project has no classification (its memory then counts as Internal). Absent for the organisation's. */
    classified: z.boolean().optional(),
    entitlement: entitlementSchema,
    /** Items the reader reads. */
    readable: z.number().int().nonnegative(),
    /** Owners only: what is hidden from the reader. An item may be hidden for both reasons, so the parts can add up to more than `total`. */
    hidden: z
        .object({
        total: z.number().int().nonnegative(),
        /** Labelled above the level the reader reads to. */
        aboveLevel: z.number().int().nonnegative(),
        /** Of those, labelled above the project's own level (a labelling rule or a person raised them). */
        aboveProject: z.number().int().nonnegative(),
        /** Carrying a compartment the reader does not hold. */
        compartments: z.number().int().nonnegative(),
        /** The compartments the reader lacks, the most used first. */
        missing: z.array(compartmentIdSchema).max(20),
        /** Hidden items by their level. */
        byLevel: z.object({ public: z.number(), internal: z.number(), confidential: z.number(), restricted: z.number() }),
    })
        .optional(),
    /**
     * Owners only: the one change that would let the reader read the most of what is hidden. `classify`: set the project's
     * classification to `level`; `compartments`: grant the project's members the compartments in `hidden.missing`;
     * `clearance`: an administrator raises the reader's clearance to `level` (or the items are labelled lower).
     */
    fix: z.object({ action: z.enum(["classify", "compartments", "clearance"]), level: sensitivitySchema.optional(), unlocks: z.number().int().nonnegative() }).optional(),
});
/** What a `memory.read` / `memory.denied` audit entry may say: labels and counts, never content. */
export const memoryAuditDetailSchema = z
    .object({
    scope: z.enum(["repo", "knowledge"]).optional(),
    via: z.string().max(200).optional(),
    labelsServed: z.array(z.object({ sensitivity: sensitivitySchema, compartments: z.array(compartmentIdSchema).max(64) })).max(200).optional(),
    itemsFiltered: z.number().int().nonnegative().optional(),
    reason: z.string().max(200).optional(),
})
    .strip();
/** Match a glob (`**` any depth, `*` within a segment, `?` one character) against a path. */
export function globMatch(glob, path) {
    let re = "";
    for (let i = 0; i < glob.length; i++) {
        const c = glob[i];
        if (c === "*") {
            if (glob[i + 1] === "*") {
                re += ".*";
                i++;
                if (glob[i + 1] === "/")
                    i++;
            }
            else
                re += "[^/]*";
        }
        else if (c === "?")
            re += "[^/]";
        else
            re += c.replace(/[.+^${}()|[\]\\]/g, "\\$&");
    }
    return new RegExp(`^${re}$`).test(path);
}
/** The label a set of rules gives an item: the union of matching compartments and the highest sensitivity. */
export function labelFor(rules, item, base) {
    let sensitivity = base;
    const compartments = new Set();
    for (const r of rules) {
        const hit = (r.match.pathGlob && item.path && globMatch(r.match.pathGlob, item.path)) || (r.match.source && item.source === r.match.source);
        if (!hit)
            continue;
        r.compartments.forEach((c) => compartments.add(c));
        if (r.sensitivity && SENSITIVITY_RANK[r.sensitivity] > SENSITIVITY_RANK[sensitivity])
            sensitivity = r.sensitivity;
    }
    return { sensitivity, compartments: [...compartments].sort() };
}
//# sourceMappingURL=memory.js.map